import React, {useEffect, useState} from 'react';
import {Input, message, Popover} from 'antd';
import dayjs from 'dayjs';
import RocDateGridPicker from './RocDateGridPicker';

const formatROCYear = (year: number): string => {
    return year < 100 ? String(year).padStart(3, '0') : String(year);
};

const formatInput = (date: dayjs.Dayjs, mode: 'yyy-mm-dd' | 'yyy-mm') => {
    if (!dayjs.isDayjs(date) || !date.isValid()) return '';
    const yyy = formatROCYear(date.year() - 1911);
    const mm = String(date.month() + 1).padStart(2, '0');
    const dd = String(date.date()).padStart(2, '0');
    return mode === 'yyy-mm' ? `${yyy}-${mm}` : `${yyy}-${mm}-${dd}`;
};

const isValidROCFormat = (input: string, mode: 'yyy-mm-dd' | 'yyy-mm'): boolean => {
    const regex = mode === 'yyy-mm-dd' ? /^\d{3}-\d{2}-\d{2}$/ : /^\d{3}-\d{2}$/;
    if (!regex.test(input)) return false;

    const [yyy, mm, dd] = input.split('-');
    const year = parseInt(yyy, 10) + 1911;
    const dateStr = mode === 'yyy-mm-dd' ? `${year}-${mm}-${dd}` : `${year}-${mm}-01`;
    return dayjs(dateStr, 'YYYY-MM-DD', true).isValid();
};

interface RocDateInputPickerProps {
    value?: dayjs.Dayjs | null;
    onChange?: (value: dayjs.Dayjs | null) => void;
    mode?: 'yyy-mm-dd' | 'yyy-mm'; // 新增格式模式
    disabled?: boolean;
}


const RocDateInputPicker: React.FC<RocDateInputPickerProps> = ({ value, onChange, mode = 'yyy-mm-dd', disabled }) => {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);

    useEffect(() => {
    if (isTyping) return;
    if (value && dayjs.isDayjs(value) && value.isValid()) {
        setInputValue(formatInput(value, mode));
    } else {
        setInputValue('');
  }
}, [value, isTyping, mode]);

    const handleChange = (date: dayjs.Dayjs | null) => {
        onChange?.(date);
        setInputValue(date ? formatInput(date, mode) : '');
        setOpen(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const filtered = raw.replace(/[^\d-]/g, ''); // 只允許數字與 -
        console.log(filtered);
        setInputValue(filtered);
        setIsTyping(true);

        if (filtered.trim() === '') {
            onChange?.(null);       // ✅ 通知 Form：值被清空
            setIsTyping(false);     // ✅ 立刻解除 typing，避免 effect 被鎖住
            setOpen(false);         // 可選：順便關掉 popover
    }
    };

    const handleBlur = () => {
        setIsTyping(false);
        const trimmed = inputValue.trim();
        if (trimmed === '') {
            onChange?.(null);   // ✅ 保險：blur 時空字串也清掉
            return;
        }

        if (!isValidROCFormat(trimmed, mode)) {
            message.error(`請輸入正確格式：${mode === 'yyy-mm' ? 'YYY-MM' : 'YYY-MM-DD'}（民國年）`);
            return;
        }

        const [yyy, mm, dd] = trimmed.split('-');
        const fullDate = `${parseInt(yyy, 10) + 1911}-${mm}-${dd ?? '01'}`;
        const parsed = dayjs(fullDate, 'YYYY-MM-DD');

        onChange?.(parsed);
    };

    return (
        <Popover
            content={
                <RocDateGridPicker
                    value={value ?? undefined}
                    onChange={handleChange}
                    mode={mode}
                />
            }
            trigger="click"
            // open={open}
            // onOpenChange={setOpen}
            open={!disabled && open} // ✅ 禁用時不開啟 Popover
            onOpenChange={(visible) => !disabled && setOpen(visible)}
            placement="bottomLeft"
        >
            <Input
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder={mode}
                onClick={() => setOpen(true)}
                // onClick={() => !disabled && setOpen(true)}
                maxLength={10}
                disabled={disabled}
                allowClear
            />
        </Popover>
    );
};

export default RocDateInputPicker;
